import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ImagingExamView } from '@wodi/shared';
import { Button, Checkbox, Notice, TextArea } from '@wodi/ui';
import { useState } from 'react';

import { amendImagingReport, signImagingReport } from '../api/imaging.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './imaging.module.css';

// Write and sign a report, or amend a signed one with a reason (docs/plans/phase2-
// radiology.md §1.1 items 4–5). One step: there are no saved drafts (plan R3). A verifier
// may verify in the same step (R7); anyone else's report waits for a radiologist.

const REASON_MIN = 5;

export function ReportEditor({
  exam,
  mode,
  mayVerify,
  onDone,
}: {
  exam: ImagingExamView;
  mode: 'write' | 'amend';
  mayVerify: boolean;
  onDone: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const current = exam.report?.current ?? null;
  const [findings, setFindings] = useState(
    mode === 'amend' ? (current?.findings ?? '') : (exam.reportTemplate ?? ''),
  );
  const [impression, setImpression] = useState(mode === 'amend' ? (current?.impression ?? '') : '');
  const [reason, setReason] = useState('');
  const [verify, setVerify] = useState(false);
  const [tried, setTried] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID());
  // The version the text was taken from, fixed when the editor opens.
  const [baseVersion] = useState(() => exam.report?.currentVersion ?? 1);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        clientRequestId: requestId,
        findings: findings.trim() === '' ? null : findings.trim(),
        impression: impression.trim(),
        verify: mayVerify && verify,
      };
      return mode === 'amend'
        ? amendImagingReport(exam.report?.id ?? '', {
            ...body,
            reason: reason.trim(),
            baseVersion,
          })
        : signImagingReport(exam.orderId, body);
    },
    onSuccess: async (next) => {
      queryClient.setQueryData(['imaging', 'exam', exam.orderId], next);
      await queryClient.invalidateQueries({ queryKey: ['imaging', 'worklist'] });
      toasts.show({
        title: mode === 'amend' ? 'Report amended' : 'Report signed',
        description: next.report?.status === 'verified' ? 'Verified.' : 'Waiting for verification.',
      });
      onDone();
    },
  });
  const impressionMissing = impression.trim() === '';
  const reasonMissing = mode === 'amend' && reason.trim().length < REASON_MIN;
  const label = mode === 'amend' ? 'Amend report' : 'Sign report';
  return (
    <div
      className={styles.form}
      role="group"
      aria-label={mode === 'amend' ? 'Amend the report' : 'Write the report'}
    >
      <TextArea
        label="Findings (optional)"
        value={findings}
        onChange={setFindings}
        rows={8}
        {...(mode === 'write' && exam.reportTemplate !== null
          ? { description: 'Started from the template in Settings > Imaging.' }
          : {})}
      />
      <TextArea
        label="Impression"
        value={impression}
        onChange={setImpression}
        rows={3}
        isRequired
        {...(tried && impressionMissing ? { errorMessage: 'Write the impression.' } : {})}
      />
      {mode === 'amend' ? (
        <TextArea
          label="Reason for the amendment"
          description="Kept with the new version. The earlier version stays readable."
          value={reason}
          onChange={setReason}
          rows={2}
          isRequired
          {...(tried && reasonMissing
            ? { errorMessage: 'Say why the report is changed, in a few words.' }
            : {})}
        />
      ) : null}
      {mayVerify ? (
        <Checkbox isSelected={verify} onChange={setVerify}>
          Verify it now as well
        </Checkbox>
      ) : (
        <p className={styles.meta}>A radiologist verifies it after you sign.</p>
      )}
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The report couldn't be signed.")}</Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Signing…"
          onPress={() => {
            setTried(true);
            if (!impressionMissing && !reasonMissing) save.mutate();
          }}
        >
          {label}
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
      {!online ? <Notice tone="info">Signing needs a connection to Wodi.</Notice> : null}
    </div>
  );
}
